import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const TOOLTIP_MAX_WIDTH = 280;
const TOOLTIP_MARGIN = 12;

function normalisiereMarkierung(markierung) {
  const text = String(markierung?.text || '').trim();
  if (!text || text.length < 3) return null;
  return {
    text,
    keyword: String(markierung?.keyword || '').trim(),
    grund: String(markierung?.grund || '').trim(),
    aktion: 'highlight',
  };
}

export function normalisiereCvMarkierungen(markierungen) {
  if (!Array.isArray(markierungen)) return [];
  const gesehen = new Set();
  return markierungen
    .map(normalisiereMarkierung)
    .filter(Boolean)
    .filter((markierung) => {
      const key = `${markierung.aktion}:${markierung.text.toLowerCase()}`;
      if (gesehen.has(key)) return false;
      gesehen.add(key);
      return true;
    });
}

function findeMarkierungStart(lowerSource, lowerNeedle) {
  try {
    const escaped = lowerNeedle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Wortgrenzen-aware: verhindert z.B. "SQL" → "NoSQL"
    const m = new RegExp('(?<![a-z0-9À-ɏ])' + escaped + '(?![a-z0-9À-ɏ])').exec(lowerSource);
    if (m !== null) return m.index;
  } catch (_) {}
  return lowerSource.indexOf(lowerNeedle);
}

function KeywordTooltip({ text, anchorRef, onClose }) {
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor || typeof window === 'undefined') return;

    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const centerX = rect.left + rect.width / 2;
    const minX = TOOLTIP_MARGIN + TOOLTIP_MAX_WIDTH / 2;
    const maxX = viewportWidth - TOOLTIP_MARGIN - TOOLTIP_MAX_WIDTH / 2;
    const x = Math.min(Math.max(centerX, minX), Math.max(minX, maxX));
    const placement = rect.top > 96 ? 'top' : 'bottom';

    setPosition({
      x,
      y: placement === 'top' ? rect.top : rect.bottom,
      placement,
      arrowX: centerX - x,
    });
  }, [anchorRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!position || !text || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={`cv-keyword-tooltip cv-keyword-tooltip--${position.placement}`}
      role="tooltip"
      style={{
        left: position.x,
        top: position.y,
        '--cv-keyword-tooltip-arrow-x': `${position.arrowX}px`,
      }}
    >
      {text}
    </div>,
    document.body
  );
}

function KeywordMark({ item, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const title = [
    item.keyword ? `Optimiert für: ${item.keyword}` : 'Stellenbezogen optimiert',
    item.grund,
  ].filter(Boolean).join(' - ');

  const show = () => {
    if (title) setOpen(true);
  };
  const hide = () => setOpen(false);

  return (
    <span
      ref={ref}
      className={`cv-keyword-mark cv-keyword-mark--${item.aktion}`}
      data-keyword={item.keyword || undefined}
      data-has-tooltip={title ? 'true' : undefined}
      aria-label={title || undefined}
      tabIndex={title ? 0 : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open && <KeywordTooltip text={title} anchorRef={ref} onClose={hide} />}
    </span>
  );
}

export function renderCvHighlighter(text, markierungen = []) {
  const source = String(text || '');
  if (!source || markierungen.length === 0) return source;

  const lowerSource = source.toLowerCase();
  const treffer = [];

  markierungen.forEach((markierung) => {
    const needle = markierung.text.toLowerCase();
    const start = findeMarkierungStart(lowerSource, needle);
    if (start < 0) return;
    treffer.push({
      ...markierung,
      start,
      end: start + markierung.text.length,
    });
  });

  if (treffer.length === 0) return source;

  treffer.sort((a, b) => a.start - b.start || b.end - a.end);
  const ohneUeberlappung = [];
  let lastEnd = -1;
  treffer.forEach((item) => {
    if (item.start < lastEnd) return;
    ohneUeberlappung.push(item);
    lastEnd = item.end;
  });

  const teile = [];
  let cursor = 0;

  ohneUeberlappung.forEach((item, index) => {
    if (item.start > cursor) {
      teile.push(source.slice(cursor, item.start));
    }
    teile.push(
      <KeywordMark
        key={`${item.start}-${item.end}-${index}`}
        item={item}
      >
        {source.slice(item.start, item.end)}
      </KeywordMark>
    );
    cursor = item.end;
  });

  if (cursor < source.length) {
    teile.push(source.slice(cursor));
  }

  return teile;
}
